import {Button, Modal, ModalBody, ModalContent, useDisclosure} from "@heroui/react";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {useEffect} from 'react';

export default function DeleteModal({data, openModal = false, ...props}) {
	const {isOpen, onOpen, onClose} = useDisclosure();
	const queryClient = useQueryClient()
	
	const onDelete = useMutation({
		mutationFn: () => repositories.project.deleteProject({id: data?.project_id}),
		onSuccess: async () => {
			await queryClient.invalidateQueries(['projects']);
			onClose();
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
		}
	})
	
	useEffect(() => {
		openModal ? onOpen() : onClose()
	}, [openModal]);
	
	return (
		<>
			<Modal
				classNames={{
					base: 'overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl',
					body: 'p-0',
					backdrop: 'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20 backdrop-blur-sm',
				}}
				size="lg"
				hideCloseButton
				isOpen={isOpen}
				onOpenChange={onClose}>
				<ModalContent>
					{(onClose) => (
						<ModalBody>
							{/* Hero band — danger themed with a pulsing trash badge */}
							<div className="relative overflow-hidden bg-gradient-to-br from-[#ef4444] via-[#f05252] to-[#fb7185] px-8 pt-9 pb-12">
								<div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
								<div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

								<div className="relative flex flex-col items-center text-center">
									<div className="relative grid h-16 w-16 place-items-center">
										<span className="absolute inset-0 rounded-2xl bg-white/30 animate-ping" />
										<span className="relative grid h-16 w-16 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
											<i className="hgi-stroke hgi-delete-03 text-3xl text-white"></i>
										</span>
									</div>
									<h2 className="mt-4 text-xl font-semibold text-white">Delete this project?</h2>
									<p className="mt-1 max-w-sm text-sm font-light text-white/80">
										This action is permanent and can&apos;t be undone.
									</p>
								</div>
							</div>

							{/* Lifted project card */}
							<div className="relative -mt-6 px-6">
								<div className="flex items-center gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4 shadow-sm">
									<div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#ef44441a] text-[#ef4444]">
										<i className="hgi-stroke hgi-folder-01 text-lg"></i>
									</div>
									<div className="min-w-0">
										<p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Project</p>
										<p className="truncate text-sm font-semibold text-slate-800">{data?.project_name}</p>
									</div>
								</div>
							</div>

							{/* Consequence note */}
							<div className="px-6 pt-4">
								<div className="flex items-start gap-3 rounded-2xl border border-[#12131714] bg-[#fcfcfd] p-4">
									<i className="hgi-stroke hgi-information-circle mt-0.5 text-lg text-slate-400"></i>
									<p className="text-sm font-light leading-relaxed text-slate-500">
										This removes the project and all of its data.
									</p>
								</div>
							</div>

							{/* Actions */}
							<div className="flex justify-end gap-3 px-6 py-5">
								<Button
									onPress={onClose}
									variant="bordered"
									className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
									Keep it
								</Button>
								<Button
									isLoading={onDelete.isPending}
									onPress={onDelete.mutate}
									className="h-11 rounded-xl bg-[#ef4444] px-6 text-sm font-medium text-white shadow-lg shadow-[#ef444440] transition hover:opacity-95">
									<i className="hgi-stroke hgi-delete-03 text-[15px]"></i> Yes, delete
								</Button>
							</div>
						</ModalBody>
					)}
				</ModalContent>
			</Modal>
		</>
	);
}
